import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  type LocalFeatureInfo,
  type LocalStepInfo,
  type Report,
  type StepClause,
  type StepLibraryEntry,
} from '../api';
import { Badge, Button, Page } from '../ui';

const TEMPLATE = `Funcionalidade: <nome da funcionalidade>

Cenário: <descreva o que está sendo testado>
  Dado que <pré-condição>
  Quando <ação>
  Então <resultado esperado>
`;

const CLAUSE_LABEL: Record<StepClause, string> = {
  given: 'Dado',
  when: 'Quando',
  then: 'Então',
};

const CLAUSE_KW_CLASS: Record<StepClause, string> = {
  given: 'kw-given',
  when: 'kw-when',
  then: 'kw-then',
};

interface PatternToken {
  kind: 'text' | 'param';
  value: string;
}

// Converte uma cucumber-expression ({string}, {int}, …) numa lista de tokens
// onde os params viram placeholders nomeados (<texto>, <número>, etc).
function tokenizeExpression(p: string): PatternToken[] {
  const out: PatternToken[] = [];
  const re = /\{([a-zA-Z_]\w*)?\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(p)) !== null) {
    if (m.index > last) out.push({ kind: 'text', value: p.slice(last, m.index) });
    const name = m[1] ?? '';
    out.push({ kind: 'param', value: paramAlias(name) });
    last = m.index + m[0].length;
  }
  if (last < p.length) out.push({ kind: 'text', value: p.slice(last) });
  return out;
}

function paramAlias(name: string): string {
  switch (name) {
    case 'string': return 'texto';
    case 'int': return 'número';
    case 'float': return 'decimal';
    case 'word': return 'palavra';
    case '': return 'valor';
    default: return name;
  }
}

// Aproxima uma regex Gherkin a tokens. Funciona pros patterns mais comuns:
// d+, S+, w+, .*, .+ e alternations. Patterns esotericos caem em "valor".
const BS = String.fromCharCode(92); // backslash literal.

function tokenizeRegex(p: string): PatternToken[] {
  let s = p;
  if (s.startsWith("^")) s = s.slice(1);
  if (s.endsWith("$")) s = s.slice(0, -1);

  const out: PatternToken[] = [];
  let buf = "";
  const flush = () => {
    if (buf) { out.push({ kind: "text", value: buf }); buf = ""; }
  };

  let i = 0;
  while (i < s.length) {
    const c = s.charAt(i);
    if (c === BS) {
      // Escape literal: usa o proximo char como texto comum.
      buf += s.charAt(i + 1);
      i += 2;
      continue;
    }
    if (c === "(") {
      // Acha o ) correspondente respeitando escapes; nao trata grupos aninhados.
      let j = i + 1;
      while (j < s.length) {
        const cj = s.charAt(j);
        if (cj === BS) { j += 2; continue; }
        if (cj === ")") break;
        j += 1;
      }
      const inner = s.slice(i + 1, j); // mantem backslashes pro regexAlias detectar d+, etc.
      flush();
      out.push({ kind: "param", value: regexAlias(inner) });
      i = j + 1;
      continue;
    }
    buf += c;
    i += 1;
  }
  flush();
  return out;
}

function regexAlias(inner: string): string {
  let t = inner.trim();
  if (t.startsWith("?:")) t = t.slice(2).trim();
  // Patterns comuns de cucumber/cypress, montados via RegExp pra evitar
  // problemas de escape no source.
  const dPlus = new RegExp("^" + BS + "d[+*]$");
  const sPlus = new RegExp("^" + BS + "S[+*]$");
  const wPlus = new RegExp("^" + BS + "w[+*]$");
  const dotAny = new RegExp("^[.][+*][?]?$"); // .+ .* .+? .*?
  const ident = new RegExp("^[a-zA-Z0-9_ -]+$");
  if (dPlus.test(t)) return "número";
  if (sPlus.test(t) || wPlus.test(t)) return "palavra";
  if (dotAny.test(t)) return "texto";
  // Alternativas tipo "dia|noite" ou identificadores simples.
  if (t.includes("|") && t.split("|").every((p) => ident.test(p.trim()))) return t;
  if (ident.test(t)) return t;
  return "valor";
}

function tokenizePattern(p: string, kind: 'expression' | 'regex'): PatternToken[] {
  return kind === 'expression' ? tokenizeExpression(p) : tokenizeRegex(p);
}

function PrettyPattern({ pattern, kind }: { pattern: string; kind: 'expression' | 'regex' }) {
  const tokens = useMemo(() => tokenizePattern(pattern, kind), [pattern, kind]);
  return (
    <span className="lf-pattern">
      {tokens.map((t, i) =>
        t.kind === 'text' ? (
          <span key={i}>{t.value}</span>
        ) : (
          <span key={i} className="lf-placeholder">{t.value}</span>
        ),
      )}
    </span>
  );
}

export function Scenarios() {
  const { slug } = useParams<{ slug: string }>();
  const [scenarios, setScenarios] = useState<Report[]>([]);
  const [steps, setSteps] = useState<StepLibraryEntry[]>([]);
  const [localFeatures, setLocalFeatures] = useState<LocalFeatureInfo[]>([]);
  const [localScanned, setLocalScanned] = useState<number | null>(null);
  const [loadingLocal, setLoadingLocal] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [clauseFilter, setClauseFilter] = useState<StepClause | 'all'>('all');
  const [gherkin, setGherkin] = useState(TEMPLATE);
  const [busy, setBusy] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const editorRef = useRef<HTMLTextAreaElement | null>(null);

  function load() {
    if (!slug) return;
    api
      .listScenarios(slug)
      .then(({ scenarios: s }) => setScenarios(s))
      .catch((e) => setErr(e.error ?? 'Falha ao carregar cenários'));
    api
      .listSteps(slug)
      .then(({ steps: s }) => setSteps(s))
      .catch((e) => setErr(e.error ?? 'Falha ao carregar steps'));
    loadLocalFeatures();
  }
  useEffect(load, [slug]);

  function loadLocalFeatures() {
    if (!slug) return;
    setLoadingLocal(true);
    setLocalErr(null);
    api
      .listLocalFeatures(slug)
      .then(({ features, filesScanned }) => {
        setLocalFeatures(features);
        setLocalScanned(filesScanned);
      })
      .catch((e: { error?: string; status?: number }) => {
        setLocalErr(
          e.error === 'project_local_repo_path_not_configured'
            ? 'Configure o caminho local do repo nas configurações pra ver as features locais.'
            : e.error === 'local_repo_path_not_found'
              ? 'Caminho local do repo não encontrado no disco.'
              : e.error ?? 'Falha ao listar features locais',
        );
      })
      .finally(() => setLoadingLocal(false));
  }

  const filteredSteps = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return steps
      .filter((s) => clauseFilter === 'all' || s.clause === clauseFilter)
      .filter((s) => !q || s.pattern.toLowerCase().includes(q) || s.sourceFile.toLowerCase().includes(q));
  }, [steps, filter, clauseFilter]);

  // Insere o step no editor: prepend "Dado/Quando/Então" + pattern, com {string}
  // virando "<string>" pra deixar visualmente óbvio que precisa ser preenchido.
  function insertStep(step: StepLibraryEntry) {
    const ta = editorRef.current;
    if (!ta) return;
    const placeholderized =
      step.patternKind === 'expression'
        ? step.pattern.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_, t) => `"<${t}>"`)
        : step.pattern; // regex: insere cru, usuário ajusta
    const line = `  ${CLAUSE_LABEL[step.clause]} ${placeholderized}\n`;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = gherkin.slice(0, start) + line + gherkin.slice(end);
    setGherkin(next);
    // Foca depois do insert pra usuário continuar digitando.
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + line.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  async function dispatch() {
    if (!slug) return;
    if (gherkin.trim().length < 20) {
      setErr('Cenário muito curto. Escreva pelo menos um Dado/Quando/Então.');
      return;
    }
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const { report } = await api.createScenario(slug, { gherkin });
      setInfo(`Cenário disparado. Card #${report.id.slice(0, 8)} no board.`);
      setGherkin(TEMPLATE);
      load();
    } catch (e: unknown) {
      const x = e as { error?: string; status?: number };
      setErr(x.error === 'gherkin_required' ? 'Não parece um Gherkin válido.' : x.error ?? `Falha (${x.status})`);
    } finally {
      setBusy(false);
    }
  }

  async function reindex() {
    if (!slug) return;
    setReindexing(true);
    setErr(null);
    setInfo(null);
    try {
      const r = await api.reindexSteps(slug);
      const dirsLabel =
        r.scannedDirs.length === 0
          ? 'nenhum step_definitions encontrado'
          : `dirs: ${r.scannedDirs.join(', ')}`;
      setInfo(
        `Reindex: ${r.filesScanned} arquivos, ${r.stepsFound} steps · ` +
          `+${r.inserted} novos, ${r.updated} atualizados, ${r.removed} removidos · ${dirsLabel}.`,
      );
      load();
    } catch (e: unknown) {
      const x = e as { error?: string; message?: string };
      setErr(x.message ?? x.error ?? 'Falha ao reindexar');
    } finally {
      setReindexing(false);
    }
  }

  if (!slug) return null;

  return (
    <Page
      title="Cenários BDD"
      subtitle="Escreva Gherkin e dispare a geração de testes E2E. Steps já indexados aparecem na biblioteca."
      fluid
    >
      {err && <div className="ui-error-banner" style={{ marginBottom: 8 }}>{err}</div>}
      {info && <div style={{ color: 'var(--color-ok)', marginBottom: 8 }}>{info}</div>}

      <LocalFeaturesPanel
        features={localFeatures}
        filesScanned={localScanned}
        loading={loadingLocal}
        error={localErr}
        onRefresh={loadLocalFeatures}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16 }}>
        {/* Sidebar — library */}
        <aside
          style={{
            background: 'var(--panel)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxHeight: 'calc(100vh - 200px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <strong style={{ fontSize: 13 }}>Library de steps ({steps.length})</strong>
            <button type="button" className="link" onClick={reindex} disabled={reindexing}>
              {reindexing ? 'Reindexando…' : 'Reindexar'}
            </button>
          </div>
          <input
            placeholder="filtrar pattern ou arquivo…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: 8,
              border: '1px solid var(--border)',
              background: 'var(--panel-2)',
              color: 'var(--text)',
              borderRadius: 6,
              font: 'inherit',
            }}
          />
          <div style={{ display: 'flex', gap: 4 }}>
            {(['all', 'given', 'when', 'then'] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setClauseFilter(c)}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  fontSize: 12,
                  background: clauseFilter === c ? 'var(--accent)' : 'var(--panel-2)',
                  color: clauseFilter === c ? 'white' : 'var(--muted)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                {c === 'all' ? 'todos' : CLAUSE_LABEL[c]}
              </button>
            ))}
          </div>
          <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filteredSteps.length === 0 && (
              <div style={{ color: 'var(--muted)', fontSize: 12, padding: 8, textAlign: 'center' }}>
                {steps.length === 0
                  ? 'Library vazia. Rode "Reindexar" depois de mergear cenários, ou implemente steps direto no repo.'
                  : 'Nenhum step bate o filtro.'}
              </div>
            )}
            {filteredSteps.map((s) => (
              <button
                key={s.id}
                type="button"
                className="lib-step"
                onClick={() => insertStep(s)}
                title={`Clique pra inserir · ${s.sourceFile}${s.sourceLine ? `:${s.sourceLine}` : ''}`}
              >
                <div className="lib-step-row">
                  <span className={`lib-step-kw ${CLAUSE_KW_CLASS[s.clause]}`}>
                    {CLAUSE_LABEL[s.clause]}
                  </span>
                  <PrettyPattern pattern={s.pattern} kind={s.patternKind} />
                </div>
                <div className="lib-step-source">
                  {s.sourceFile}{s.sourceLine ? `:${s.sourceLine}` : ''}
                  {s.patternKind === 'regex' && <span className="lib-step-regex-tag">regex</span>}
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Editor + lista de cenários */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <section>
            <h2 style={{ fontSize: 14, margin: '0 0 8px' }}>Novo cenário</h2>
            <textarea
              ref={editorRef}
              value={gherkin}
              onChange={(e) => setGherkin(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%',
                minHeight: 320,
                padding: 12,
                background: 'var(--panel-2)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                font: '13px ui-monospace, SFMono-Regular, Menlo, monospace',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="link"
                onClick={() => setGherkin(TEMPLATE)}
                disabled={busy}
              >
                limpar
              </button>
              <button
                type="button"
                onClick={dispatch}
                disabled={busy}
                style={{
                  padding: '8px 16px',
                  background: 'var(--accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  font: 'inherit',
                  fontWeight: 600,
                }}
              >
                {busy ? 'Disparando…' : 'Disparar Claude'}
              </button>
            </div>
          </section>

          <section>
            <h2 style={{ fontSize: 14, margin: '0 0 8px' }}>Cenários ({scenarios.length})</h2>
            {scenarios.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                Nenhum cenário ainda. Escreva o primeiro acima.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px' }}>título</th>
                    <th style={{ padding: '6px 8px' }}>status</th>
                    <th style={{ padding: '6px 8px' }}>PR</th>
                    <th style={{ padding: '6px 8px' }}>criado</th>
                  </tr>
                </thead>
                <tbody>
                  {scenarios.map((s) => (
                    <tr key={s.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px' }}>
                        <Link to={`/p/${slug}/r/${s.id}`} style={{ color: 'var(--text)' }}>
                          {s.title}
                        </Link>
                      </td>
                      <td style={{ padding: '8px', color: 'var(--muted)' }}>{s.status}</td>
                      <td style={{ padding: '8px' }}>
                        {s.prUrl ? (
                          <a href={s.prUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>
                            #{s.prNumber}
                          </a>
                        ) : (
                          <span style={{ color: 'var(--muted)' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '8px', color: 'var(--muted)' }}>
                        {new Date(s.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      </div>
    </Page>
  );
}

// Mapeia o keyword (en/pt) pra uma classe semântica de cor.
function stepKeywordClass(kw: string): string {
  const k = kw.toLowerCase();
  if (k === 'given' || k === 'dado') return 'kw-given';
  if (k === 'when' || k === 'quando') return 'kw-when';
  if (k === 'then' || k === 'então' || k === 'entao') return 'kw-then';
  if (k === 'and' || k === 'e' || k === 'but' || k === 'mas') return 'kw-and';
  return 'kw-other';
}

function LocalFeaturesPanel({
  features,
  filesScanned,
  loading,
  error,
  onRefresh,
}: {
  features: LocalFeatureInfo[];
  filesScanned: number | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  // Set de paths abertos. Default: primeira feature aberta quando lista chega.
  const [openPaths, setOpenPaths] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (features.length > 0 && openPaths.size === 0) {
      setOpenPaths(new Set([features[0]!.path]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features.length === 0 ? '' : features[0]?.path]);

  function toggle(p: string) {
    setOpenPaths((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  }
  const expandAll = () => setOpenPaths(new Set(features.map((f) => f.path)));
  const collapseAll = () => setOpenPaths(new Set());

  const totalScenarios = features.reduce((acc, f) => acc + f.scenarios.length, 0);
  const totalSteps = features.reduce(
    (acc, f) => acc + f.scenarios.reduce((s, sc) => s + sc.steps.length, 0),
    0,
  );

  return (
    <section className="lf-panel">
      <div className="lf-head">
        <h3>Suites de testes <span style={{ color: 'var(--color-fg-faint)', fontWeight: 400 }}>(.features)</span></h3>
        <span className="lf-meta">
          {features.length} suítes · {totalScenarios} cenários · {totalSteps} steps
          {filesScanned !== null && filesScanned !== features.length && ` (${filesScanned} encontrados)`}
        </span>
        <span className="lf-spacer" />
        {features.length > 0 && (
          <div className="lf-toolbar">
            <button type="button" className="link" onClick={expandAll}>expandir tudo</button>
            <button type="button" className="link" onClick={collapseAll}>recolher tudo</button>
          </div>
        )}
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading}>
          {loading ? 'Atualizando…' : 'Atualizar'}
        </Button>
      </div>

      {error && <div className="ui-error-banner" style={{ marginBottom: 8 }}>{error}</div>}

      {!error && features.length === 0 && !loading && (
        <div className="lf-empty">
          Nenhum <code>.feature</code> encontrado no repo local.
        </div>
      )}

      {features.length > 0 && (
        <div className="lf-list">
          {features.map((f) => {
            const isOpen = openPaths.has(f.path);
            return (
              <div key={f.path} className={`lf-suite ${isOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className="lf-suite-head"
                  onClick={() => toggle(f.path)}
                  aria-expanded={isOpen}
                >
                  <span className="lf-suite-caret">{isOpen ? '▾' : '▸'}</span>
                  <span className="lf-suite-titlebox">
                    <div className={`lf-suite-name ${f.name ? '' : 'unset'}`}>
                      {f.name || '(sem header Feature)'}
                    </div>
                    <div className="lf-suite-path">{f.path}</div>
                  </span>
                  <Badge variant="neutral">
                    {f.scenarios.length} cenário{f.scenarios.length === 1 ? '' : 's'}
                  </Badge>
                  {f.tags.length > 0 && (
                    <span className="lf-suite-tags">
                      {f.tags.map((t) => (
                        <Badge key={t} variant="accent" mono>{t}</Badge>
                      ))}
                    </span>
                  )}
                </button>

                {isOpen && (
                  <div className="lf-suite-body">
                    {f.description && <div className="lf-desc">{f.description}</div>}
                    {f.scenarios.length === 0 ? (
                      <div className="lf-empty-steps">Suíte sem cenários.</div>
                    ) : (
                      <div className="lf-scenarios">
                        {f.scenarios.map((s, i) => (
                          <ScenarioBlock key={i} scenario={s} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ScenarioBlock({ scenario }: { scenario: LocalFeatureInfo['scenarios'][number] }) {
  return (
    <div className="lf-scenario">
      <div className="lf-scenario-head">
        <span className="lf-scenario-line">:{scenario.line}</span>
        <span className="lf-scenario-name">{scenario.name}</span>
        {scenario.outline && <span className="lf-scenario-outline">outline</span>}
        {scenario.tags.map((t) => (
          <Badge key={t} variant="accent" mono>{t}</Badge>
        ))}
      </div>
      {scenario.steps.length === 0 ? (
        <div className="lf-empty-steps">(sem steps)</div>
      ) : (
        <div className="lf-steps">
          {scenario.steps.map((st, i) => (
            <StepLine key={i} step={st} />
          ))}
        </div>
      )}
    </div>
  );
}

function StepLine({ step }: { step: LocalStepInfo }) {
  return (
    <div className="lf-step">
      <span className={`lf-step-kw ${stepKeywordClass(step.keyword)}`}>{step.keyword}</span>
      <span className="lf-step-text">{step.text}</span>
    </div>
  );
}
